import { subscribeInventoryChanges } from '../collection/inventoryInvalidation';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  useSyncExternalStore,
} from 'react';
import { createBillingSummaryStore } from './billingSummaryStore';

const Context = createContext<ReturnType<
  typeof createBillingSummaryStore
> | null>(null);
export function BillingSummaryProvider({ children }: { children: ReactNode }) {
  const [store] = useState(createBillingSummaryStore);
  return <Context.Provider value={store}>{children}</Context.Provider>;
}
export function useBillingSummary(userId?: string, session = false) {
  const shared = useContext(Context);
  const [local] = useState(createBillingSummaryStore);
  const store = shared || local;
  useLayoutEffect(() => {
    if (session || !shared) store.setAccount(userId);
  }, [store, userId, session, shared]);
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot);
  useEffect(() => {
    if (!session || !userId) return;
    const refresh = () => {
      if (document.visibilityState !== 'hidden') void store.refresh(userId);
    };
    refresh();
    let mutationTimer: ReturnType<typeof setTimeout>;
    const unsubscribe = subscribeInventoryChanges(userId, () => {
      clearTimeout(mutationTimer);
      mutationTimer = setTimeout(() => {
        void store.refresh(userId, true);
      }, 300);
    });
    window.addEventListener('focus', refresh);
    window.addEventListener('online', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      unsubscribe();
      clearTimeout(mutationTimer);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('online', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [store, userId, session]);
  return {
    store,
    snapshot:
      snapshot.userId === userId
        ? snapshot
        : {
            loading: false,
            billing: undefined,
            wallet: undefined,
            error: undefined,
          },
  };
}
